import { green } from "@mui/material/colors";
import { keyframes, styled } from "@mui/material/styles";
import React, { JSX } from "react";
import TextChange from "work/proposals/redlining/__diff/text-change";

const settleAddedText = keyframes`
  0%, 70% {
    font-weight: 500;
  }
  100% {
    font-weight: 400;
  }
`;

const collapseRemovedText = keyframes`
  0%, 60% {
    max-width: 24rem;
    opacity: 1;
    font-size: inherit;
    line-height: inherit;
    margin-right: 0;
  }
  100% {
    max-width: 0;
    opacity: 0;
    font-size: 0;
    line-height: 0;
    margin-right: 0;
  }
`;

const DiffResult = styled("span", {
  shouldForwardProp: (prop) => !["change", "allResolved", "acceptedResolved", "animateAcceptedResolved"].includes(prop.toString()),
})<{
  change?: TextChange;
  allResolved?: boolean;
  acceptedResolved?: boolean;
  animateAcceptedResolved?: boolean;
}>(
  ({change, allResolved, acceptedResolved, animateAcceptedResolved, theme}) => ({
    backgroundColor: (function () {
      if (allResolved) return "unset";
      if (change?.diff.added) return green[300];
    })(),
    color: (function () {
      if (change?.diff.removed && acceptedResolved) return theme.palette.text.disabled;
      if (change?.diff.added && acceptedResolved) return theme.palette.text.primary;
      if (change?.diff.added && allResolved) return theme.palette.primary.main;
      return theme.palette.text.primary;
    })(),
    fontWeight: (function () {
      if (acceptedResolved && change?.diff.added) return 500;
      if (acceptedResolved && (change?.diff.removed || change?.isModified)) return 500;
      return "inherit";
    })(),
    fontSynthesis: (function (): string | undefined {
      if (acceptedResolved && change?.diff.added) return "weight";
      return undefined;
    })(),
    display: change?.diff.removed && animateAcceptedResolved ? "inline-block" : "inline",
    overflow: change?.diff.removed && animateAcceptedResolved ? "hidden" : "visible",
    verticalAlign: "bottom",
    textDecoration: (function () {
      if (change?.diff.removed) return "line-through";
    })(),
    textDecorationColor: (function () {
      if (change?.diff.removed && acceptedResolved) return theme.palette.text.primary;
      if (change?.diff.removed && allResolved) return theme.palette.primary.main;
      if (change?.diff.removed) return theme.palette.error.main;
      return "initial";
    })(),
    textDecorationThickness: '2px',
    '&:hover': {
      textDecorationColor: (function() { 
        let color = "initial";
        if (change?.diff.removed && acceptedResolved) color = theme.palette.text.primary;
        if (change?.diff.removed && allResolved) color = theme.palette.primary.main;
        if (change?.diff.removed && !allResolved) color = theme.palette.error.main;
        return `oklch(from ${color} l c h / 0.25)`
      }())
    },
    animation: (function (): string | undefined {
      if (animateAcceptedResolved && change?.diff.added) return `${settleAddedText} 1s ease-out forwards`;
      if (animateAcceptedResolved && change?.diff.removed) return `${collapseRemovedText} 1s ease-out forwards`;
      return undefined;
    })(),
  }));
const TextContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "singleLine",
})<{ singleLine?: boolean }>(
  ({singleLine, theme}) => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    lineHeight: 1.5,
    paddingRight: theme.spacing(2),
    width: "100%",
    // One line, cut off with an ellipsis. A block rather than a flex column, because an ellipsis is
    // drawn by the box whose inline content overflows it — the removed and added words have to be that
    // content, not the contents of a flex item.
    ...(singleLine && {
      display: "block",
      overflow: "hidden",
      paddingRight: 0,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
  }));
const ChangeGroup = styled("span")(
  () => ({
    whiteSpace: "inherit"
  }));

export interface DiffTextProps {
  className?: string;
  textChanges: TextChange[];
  allResolved?: boolean;
  acceptedResolved?: boolean;
  animateAcceptedResolved?: boolean;
  /** Draws the diff on one line, cut off with an ellipsis where it does not fit — a table cell's shape. */
  singleLine?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

/**
 * Renders a field's word diff. Presentation only: it paints added/removed/modified segments and
 * nothing more.
 *
 * It used to hang a per-word Accept/Reject/Edit popover off each segment. Canonical negotiation
 * decisions are field-atomic — a partial, word-level decision has no representation on the wire, and
 * `docs/design/proposals-negotiation/ARCHITECTURE.md` requires a versioned text-operation/CRDT
 * contract before one could — so the popover was removed rather than left behind a flag. Whole-field
 * Accept/Reject/Edit lives on the redline inputs that render this component.
 */
export default function DiffText(props: Readonly<DiffTextProps>) {
  const {
    className,
    textChanges: changes,
    allResolved,
    acceptedResolved,
    animateAcceptedResolved,
    singleLine,
    onClick
  } = props;

  const getChangeType = (change: TextChange): "added" | "removed" | "unchanged" => {
    if (change.diff.added) {
      return "added";
    }
    if (change.diff.removed) {
      return "removed";
    }
    return "unchanged";
  };

  const getChangeKey = (change: TextChange): string =>
    `${change.id}_${change?.diff.value}_${getChangeType(change)}`;

  function renderChanges(changes: TextChange[]) {
    let nodes: JSX.Element[] = [];

    for (let changeIndex = 0; changeIndex < changes.length; changeIndex++) {
      if (
        changes?.[changeIndex - 1]?.isModified &&
        changes[changeIndex].isModified
      ) {
        continue;
      }
      if (
        changes[changeIndex].isModified &&
        changes[changeIndex + 1]?.isModified
      ) {
        const node = (
          <ChangeGroup key={getChangeKey(changes[changeIndex + 1])}>
            <DiffResult
              change={changes[changeIndex]}
              data-diff-kind={getChangeType(changes[changeIndex])}
              allResolved={allResolved}
              acceptedResolved={acceptedResolved}
              animateAcceptedResolved={animateAcceptedResolved}
            >
              {changes[changeIndex]?.diff.value}
            </DiffResult>
            <DiffResult
              change={changes[changeIndex + 1]}
              data-diff-kind={getChangeType(changes[changeIndex + 1])}
              allResolved={allResolved}
              acceptedResolved={acceptedResolved}
              animateAcceptedResolved={animateAcceptedResolved}
            >
              {changes[changeIndex + 1]?.diff.value}
            </DiffResult>
          </ChangeGroup>
        );
        nodes.push(node);
        continue;
      }

      const node = (
        <DiffResult
          key={getChangeKey(changes[changeIndex])}
          change={changes[changeIndex]}
          data-diff-kind={getChangeType(changes[changeIndex])}
          allResolved={allResolved}
          acceptedResolved={acceptedResolved}
          animateAcceptedResolved={animateAcceptedResolved}
        >
          {changes[changeIndex]?.diff.value}
        </DiffResult>
      );
      nodes.push(node);
    }

    return <span>{nodes.map((node) => node)}</span>;
  }

  return (
    <TextContainer className={className} singleLine={singleLine} onClick={onClick}>
      {renderChanges(changes)}
    </TextContainer>
  );
}
